export const MAX_IMAGE_UPLOAD_LONG_EDGE = 2000;
const DEFAULT_IMAGE_UPLOAD_QUALITY = 0.9;

export function resolveConstrainedImageDimensions(
  width: number,
  height: number,
  maxLongEdge: number = MAX_IMAGE_UPLOAD_LONG_EDGE,
) {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0 ||
    maxLongEdge <= 0
  ) {
    return { width: 1, height: 1, didScaleDown: false };
  }

  const longestEdge = Math.max(width, height);
  if (longestEdge <= maxLongEdge) {
    return {
      width: Math.round(width),
      height: Math.round(height),
      didScaleDown: false,
    };
  }

  const scale = maxLongEdge / longestEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    didScaleDown: true,
  };
}

function replaceFileExtension(filename: string, nextExtension: string) {
  const trimmedExtension = nextExtension.replace(/^\./, "");
  if (!filename.includes(".")) {
    return `${filename}.${trimmedExtension}`;
  }
  return filename.replace(/\.[^.]+$/, `.${trimmedExtension}`);
}

function loadImageFromObjectUrl(objectUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Could not load selected image for preprocessing"));
    image.src = objectUrl;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  outputType: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), outputType, quality);
  });
}

export async function prepareImageUploadFile(
  file: File,
  options?: {
    maxLongEdge?: number;
    quality?: number;
  },
) {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !file.type.startsWith("image/")
  ) {
    return file;
  }

  const maxLongEdge = options?.maxLongEdge ?? MAX_IMAGE_UPLOAD_LONG_EDGE;
  const quality = options?.quality ?? DEFAULT_IMAGE_UPLOAD_QUALITY;
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageFromObjectUrl(objectUrl);
    const { width, height, didScaleDown } = resolveConstrainedImageDimensions(
      image.naturalWidth || image.width,
      image.naturalHeight || image.height,
      maxLongEdge,
    );

    if (!didScaleDown) {
      return file;
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }

    context.drawImage(image, 0, 0, width, height);

    const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await canvasToBlob(canvas, outputType, quality);
    if (!blob) {
      return file;
    }

    const resizedFile = new File(
      [blob],
      replaceFileExtension(file.name, outputType === "image/png" ? "png" : "jpg"),
      {
        type: outputType,
        lastModified: file.lastModified,
      },
    );

    return resizedFile.size < file.size ? resizedFile : file;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
